import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { useNavigate } from "react-router-dom";
import { MessageSquare, ListTodo, Users, Briefcase, Inbox } from "lucide-react";
import Avatar from "@/components/Avatar";
import { Button } from "@/components/ui/button";

/**
 * Generic Home dashboard — works for any organization. No logistics-specific
 * cards, no BMS, no shipment counts, no operational escalations. Every org
 * sees the same six sections: presence, quick counts, mentions, tasks.
 */
export default function HomeView({ onOpenPeople, presence }) {
  const { user, currentOrg } = useAuth();
  const [mentions, setMentions] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [requests, setRequests] = useState([]);
  const [channels, setChannels] = useState([]);
  const [people, setPeople] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!currentOrg?.id) return;
    // Reset any stale state from the previously-active org before refetching.
    setMentions([]); setTasks([]); setProjects([]); setRequests([]); setChannels([]); setPeople(null);
    Promise.all([
      api.get("/mentions").catch(() => ({ data: [] })),
      api.get("/tasks", { params: { assignee: "me" } }).catch(() => ({ data: [] })),
      api.get("/projects", { params: { status: "active" } }).catch(() => ({ data: [] })),
      api.get("/requests").catch(() => ({ data: [] })),
      api.get("/channels").catch(() => ({ data: [] })),
      api.get("/people").catch(() => ({ data: null })),
    ]).then(([m, t, p, r, c, pe]) => {
      setMentions(m.data.slice(0, 4));
      setTasks(t.data.slice(0, 5));
      setProjects(p.data);
      setRequests(r.data);
      setChannels(c.data);
      setPeople(pe.data);
    });
  }, [currentOrg?.id, presence]);

  const unread = channels.reduce((a, c) => a + (c.unread || 0), 0);
  const pendingTasks = tasks.filter((t) => t.status !== "done").length;
  const openRequests = requests.filter((r) => ["Submitted", "In Review", "In Progress", "Waiting for Information"].includes(r.status)).length;

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white" data-testid="home-view">
      <div className="h-14 border-b flex items-center px-6 shrink-0">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Home</div>
          <h1 className="text-base font-semibold">Welcome back, {user?.name?.split(" ")[0]}</h1>
        </div>
      </div>
      <div className="flex-1 overflow-auto scroll-thin p-6 space-y-6 max-w-5xl">
        {people && (
          <div className="rounded-xl border border-zinc-200 bg-white p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Your team right now</div>
              <Button size="sm" variant="ghost" onClick={onOpenPeople} data-testid="home-open-people"><Users className="w-4 h-4 mr-1"/> Open People</Button>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <PresencePill label="Online" v={people.summary.online} color="#10B981"/>
              <PresencePill label="In meetings" v={people.summary.meeting} color="#8B5CF6"/>
              <PresencePill label="Away" v={people.summary.away} color="#F59E0B"/>
              <PresencePill label="Offline" v={people.summary.offline} color="#9CA3AF"/>
            </div>
            {people.groups.online.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {people.groups.online.slice(0, 12).map((p) => (
                  <div key={p.user_id} className="flex items-center gap-2 rounded-full border border-zinc-200 pl-1 pr-3 py-1">
                    <Avatar user={p} size="sm" status={p.status} online/>
                    <span className="text-xs">{p.name}</span>
                    {p.room && <span className="text-[10px] text-zinc-500">· {p.room}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-4 gap-3" data-testid="home-quick-stats">
          <SmallCard onClick={() => nav("/app/notifications")} label="Unread messages" value={unread} icon={MessageSquare}/>
          <SmallCard onClick={() => nav("/app/tasks")} label="My tasks pending" value={pendingTasks} icon={ListTodo}/>
          <SmallCard onClick={() => nav("/app/projects")} label="Active projects" value={projects.length} icon={Briefcase}/>
          <SmallCard onClick={() => nav("/app/requests")} label="Open requests" value={openRequests} icon={Inbox}/>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Recent mentions</div>
            <div className="space-y-2">
              {mentions.length === 0 && <div className="text-sm text-zinc-400">No mentions yet.</div>}
              {mentions.map((m) => (
                <div key={m.id} className="p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Avatar user={m.user} size="sm"/>
                    <div className="text-xs text-zinc-500">{m.user?.name}</div>
                  </div>
                  <div className="text-sm mt-1">{m.text}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Your tasks</div>
            <div className="space-y-2">
              {tasks.length === 0 && <div className="text-sm text-zinc-400">No tasks assigned to you.</div>}
              {tasks.map((t) => (
                <div key={t.id} onClick={() => nav("/app/tasks")} className="p-3 border rounded-lg cursor-pointer hover:bg-zinc-50">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">{t.title}</div>
                    <span className="text-[10px] rounded-full px-2 py-0.5 bg-zinc-100">{t.status}</span>
                  </div>
                  <div className="text-xs text-zinc-500">{t.due_date || "No due date"}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PresencePill({ label, v, color }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50/70 p-3">
      <div className="text-2xl font-semibold flex items-center gap-2 tabular-nums">
        <span className="w-2 h-2 rounded-full" style={{ background: color }}/>{v}
      </div>
      <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
}

function SmallCard({ label, value, icon: Icon, onClick }) {
  return (
    <button onClick={onClick} className="text-left p-4 rounded-xl border hover:border-zinc-400 transition bg-white">
      <Icon className="w-4 h-4 mb-2 text-zinc-500"/>
      <div className="text-xs uppercase text-zinc-500">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </button>
  );
}
